/**
 * Lazy-loads web/public/v2/figaro.js only when a Figaro v3 route is opened.
 * Keeps Preact out of the main app.js bundle.
 */

let loadPromise = null;
let unmount = null;

function figaroV3Enabled() {
  const v = window.__CONFIG__?.FIGARO_V3;
  return v === true || String(v || "").toLowerCase() === "true" || String(v) === "1";
}

function figaroLegacyEnabled() {
  const v = window.__CONFIG__?.FIGARO_LEGACY;
  if (v === undefined || v === null || v === "") return true;
  return v === true || String(v).toLowerCase() === "true" || String(v) === "1";
}

function buildId() {
  return String(window.__CONFIG__?.WEB_BUILD_ID || window.__ACTRACKER_BUILD_ID__ || Date.now());
}

function isLegacyRoute(routeKey) {
  const key = String(routeKey || "");
  return key === "figaro/legacy" || key.startsWith("figaro/legacy/");
}

function isInvoiceAuditRoute(routeKey) {
  const key = String(routeKey || "");
  return key === "figaro/invoice-audit" || key.startsWith("figaro/invoice-audit/");
}

/** Map #/figaro/legacy/... → legacy renderer key. */
export function legacyRouteKey(routeKey) {
  const key = String(routeKey || "");
  if (key === "figaro/legacy") return "figaro";
  if (key.startsWith("figaro/legacy/")) {
    const rest = key.slice("figaro/legacy/".length);
    if (rest === "mpds" || rest.startsWith("mpds/")) return `figaro/workscopes/${rest}`;
    if (rest === "chat") return "figaro";
    return `figaro/${rest}`;
  }
  return key;
}

export function shouldMountFigaroV3(routeKey) {
  if (!figaroV3Enabled()) return false;
  if (isLegacyRoute(routeKey)) return false;
  if (isInvoiceAuditRoute(routeKey)) return false;
  const key = String(routeKey || "");
  return key === "figaro" || key.startsWith("figaro/");
}

export function isFigaroLegacyRoute(routeKey) {
  return isLegacyRoute(routeKey) && figaroLegacyEnabled();
}

async function loadBundle() {
  if (!loadPromise) {
    const href = `/v2/figaro.js?v=${encodeURIComponent(buildId())}`;
    loadPromise = import(/* @vite-ignore */ href).catch((err) => {
      loadPromise = null;
      throw err;
    });
  }
  return loadPromise;
}

/**
 * @returns {HTMLElement} host node for the main mount
 */
export function renderFigaroV3Host(el, { routeKey, fetchJson, apiUrl }) {
  const host = el("div", {
    id: "figaro-v3-root",
    class: "w-full",
    "data-route": String(routeKey || "figaro"),
  });
  const status = el("div", {
    class: "text-sm text-gray-500 p-4",
    text: "Loading Figaro…",
  });
  host.appendChild(status);

  void (async () => {
    try {
      const mod = await loadBundle();
      const mount = mod.mountFigaroV3 || mod.default?.mountFigaroV3;
      if (typeof mount !== "function") throw new Error("figaro.js missing mountFigaroV3");
      if (unmount) {
        try {
          unmount();
        } catch {
          /* ignore */
        }
        unmount = null;
      }
      host.replaceChildren();
      unmount = mount(host, { routeKey, fetchJson, apiUrl }) || null;
    } catch (e) {
      host.replaceChildren(
        el("div", {
          class: "text-sm text-red-700 p-4",
          text: e?.message || "Failed to load Figaro workspace",
        })
      );
    }
  })();

  return host;
}

export { figaroV3Enabled, figaroLegacyEnabled };
